import { useState, useEffect, useCallback } from 'react';

import { 
  createDevis, validateDevis, unvalidateDevis, getDevisById, updateDevis, deleteDevis,
  getAllDevis, getDevisByClient, getDevisByCommercial, addItemToDevis,
  deleteItemFromDevis, updateItemInDevis, getItemsInDevis, transferDevis, saveDevis,
  duplicateDevis
} from '../models/devisAPI'; // Adjust the import path as necessary

export const useCreateDevis = () => {
  const [error, setError] = useState('');
  const [isCreated, setIsCreated] = useState(false);

  const handleCreate = async (devisData) => {
    try {
      
      const devis = await createDevis(devisData);
      setIsCreated(true);
      return devis.data;
    } catch (err) {
      setError(err.message);
      setIsCreated(false);
    }
  };

  return { handleCreate, error, isCreated };
};

// Add these new hooks to your existing devisHooks.js

export const useTransferDevis = () => {
  const [error, setError] = useState('');
  const [isTransferred, setIsTransferred] = useState(false);

  const transfer = async (refDevis) => {
    try {
      await transferDevis(refDevis);
      setIsTransferred(true);
    } catch (err) {
      setError(err.message);
      setIsTransferred(false);
    }
  };

  return { transfer, error, isTransferred };
};

export const useDuplicateDevis = () => {
  const [error, setError] = useState('');
  const [isDuplicated, setIsDuplicated] = useState(false);
  const [newDevis, setNewDevis] = useState(null);

  const duplicate = async (id) => {
    try {
      const res = await duplicateDevis(id);
      setIsDuplicated(true);
      setNewDevis(res.data);
      return res.data;
    } catch (err) {
      setError(err.message);
      setIsDuplicated(false);
      throw err;
    }
  };

  return { duplicate, error, isDuplicated, newDevis };
};

// Update the useValidateDevis hook if necessary
export const useValidateDevis = () => {
  const [error, setError] = useState('');
  const [isValidated, setIsValidated] = useState(false);

  const validate = async (refDevis) => {
    setError('');
    try {
      const response = await validateDevis(refDevis);
      if (response?.data?.VALIDER !== true) {
        throw new Error(response?.data?.message || 'Le devis n’a pas été validé.');
      }
      setIsValidated(true);
      return response.data;
    } catch (err) {
      const reason = err.response?.data?.message || err.message;
      setError(reason);
      setIsValidated(false);
      throw new Error(reason);
    }
  };

  return { validate, error, isValidated };
};

export const useUnvalidateDevis = () => {
  const [error, setError] = useState('');
  const [isUnvalidated, setIsUnvalidated] = useState(false);

  const unvalidate = async (refDevis) => {
    try {
      await unvalidateDevis(refDevis);
      setIsUnvalidated(true);
    } catch (err) {
      setError(err.message);
      setIsUnvalidated(false);
      throw err;
    }
  };

  return { unvalidate, error, isUnvalidated };
};


export const useFetchDevisById = (id) => {
  const [devis, setDevis] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
 
  // Define the fetching logic as a callable function using useCallback
  const fetchDevis = useCallback(() => {
    if (id) {
      setLoading(true);
      setError(''); // Reset error state before new fetch attempt
      getDevisById(id)
        .then(response => {
          setDevis(response.data);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [id]);

  // Invoke fetchDevis when the component mounts and when id changes
  useEffect(() => {
    fetchDevis();
  }, [fetchDevis]);

  // Return all the state management vars and the refetch function
  return { devis, loading, error, refetch: fetchDevis };
};


export const useUpdateDevis = () => {
  const [error, setError] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);

  const handleUpdate = async (id, devisData) => {
    try {
      await updateDevis(id, devisData);
      setIsUpdated(true);
    } catch (err) {
      setError(err.message);
      setIsUpdated(false);
    }
  };

  return { handleUpdate, error, isUpdated };
};

export const useAddItemToDevis = () => {
  const [error, setError] = useState('');
  const [isAdded, setIsAdded] = useState(false);

  const handleAddItem = async (refDevis, itemData) => {
    try {
      await addItemToDevis(refDevis, itemData);
      setIsAdded(true);
    } catch (err) {
      setError(err.message);
      setIsAdded(false);
    }
  };

  return { handleAddItem, error, isAdded };
};



export const useDeleteDevis = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDeleted, setIsDeleted] = useState(false);

  const handleDelete = async (id) => {
    setIsLoading(true);
    setError(null);
    setIsDeleted(false);

    try {
      const result = await deleteDevis(id);
      setIsDeleted(true);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { handleDelete, isLoading, error, isDeleted };
};



export const useFetchAllDevis = (params) => {
  const [devis, setDevis] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDevis = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { devis: fetchedDevis, total: fetchedTotal } = await getAllDevis(params);
      setDevis(fetchedDevis);
      setTotal(fetchedTotal);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchDevis();
  }, [fetchDevis]);

  return {
    devis,
    total,
    loading,
    error,
    refetch: fetchDevis
  };
};


// Example for useFetchDevisByClient
export const useFetchDevisByClient = (clientId) => {
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (clientId) {
      setLoading(true);
      getDevisByClient(clientId)
        .then(response => {
          setDevis(response.data);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [clientId]);

  return { devis, loading, error };
};

export const useFetchDevisByCommercial = (commercialId) => {
    const [devis, setDevis] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
  
    useEffect(() => {
      if (commercialId) {
        setLoading(true);
        getDevisByCommercial(commercialId)
          .then(response => {
            setDevis(response.data);
            setLoading(false);
          })
          .catch(err => {
            setError(err.message);
            setLoading(false);
          });
      }
    }, [commercialId]);
  
    return { devis, loading, error };
  };



  
  export const useDeleteItemFromDevis = () => {
    const [error, setError] = useState('');
    const [isDeleted, setIsDeleted] = useState(false);
  
    const deleteItem = async (refDevis, codeArt) => {
      try {
        await deleteItemFromDevis(refDevis, codeArt);
        setIsDeleted(true);
      } catch (err) {
        setError(err.message);
        setIsDeleted(false);
      }
    };
  
    return { deleteItem, error, isDeleted };
  };

  
  export const useUpdateItemInDevis = () => {
    const [error, setError] = useState('');
    const [isUpdated, setIsUpdated] = useState(false);
  
    const updateItem = async (refDevis, codeArt, itemData) => {
      try {
        await updateItemInDevis(refDevis, codeArt, itemData);
        setIsUpdated(true);
      } catch (err) {
        setError(err.message);
        setIsUpdated(false);
      }
    };
  
    return { updateItem, error, isUpdated };
  };

  
  
  export const useFetchItemsInDevis = (refDevis, fetchTrigger) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    console.log("hook;",items);
    const fetchItems = useCallback(() => {
      if (refDevis) {
        setLoading(true);
        getItemsInDevis(refDevis)
          .then(response => {
            setItems(response.data);
            setLoading(false);
          })
          .catch(err => {
            setError(err.message);
            setLoading(false);
          });
      }
    }, [refDevis]);
  
    useEffect(() => {
      fetchItems();
    }, [fetchItems, fetchTrigger]);
  
    const updateItemsLocally = useCallback((updatedItem) => {
      setItems(prevItems => 
        prevItems.map(item => 
          item.CODE_ART === updatedItem.CODE_ART ? { ...item, ...updatedItem } : item
        )
      );
    }, []);
  
    return { 
      items, 
      loading, 
      error, 
      refetch: fetchItems,
      updateItemsLocally  // This function will be used for optimistic updates
    };
  };


  export const useSaveDevis = () => {
    const [error, setError] = useState('');
    const [isSaved, setIsSaved] = useState(false);
  
    const handleSaveDevis = async (data) => {
      try {
        // Ensure that data contains both devis and items
        if (!data.devis || !data.items) {
          throw new Error('Invalid data structure. Both devis and items are required.');
        }
  
        // Call the API function to save the devis
        await saveDevis(data);
        
        setIsSaved(true);
        setError('');
      } catch (err) {
        setError(err.message);
        setIsSaved(false);
      }
    };
  
    return { handleSaveDevis, error, isSaved };
  };
  
  
